import React, {ReactNode, useEffect, useState} from 'react'
import ReactMarkdown from 'react-markdown'
import {getAppOrigin} from '@salesforce/pwa-kit-react-sdk/utils/url'
import {PDPAwardSlot, ProductInformation} from '../../../types/cms'
import {Box, BoxProps, Flex, Stack, Text, useDisclosure} from '../../../vanilla'
import {CollapsablePanel} from '../../collapsable-panel'
import {ChevronUpIcon} from '../../icons'
import {TabPanel, Tabs} from '../../tabs'
import FeefoReviews from '../../feefo-reviews'
import {NutritionalModal} from './nutritional-modal'
import {useProductDetail} from '../../../store/hooks/useProductDetail'
import {CookingInstructions} from './cooking-instructions'
import {infoLeftColumn, infoRightColumn, pdpAccordionToggle} from './styles.css'
import {useCustomerStore, useGlobalStore, useContentStore} from '../../../store/hooks/useStore'
import {Slot} from '../../cms/Slot'
import {SlotContentItem} from '../../../types/cms'

interface ProductDetailAccordionProps extends BoxProps<'div'> {
  defaultOpen?: boolean
  children: ReactNode
  title: string
}

const ProductDetailAccordion = React.forwardRef((props: ProductDetailAccordionProps,  ref: React.Ref<any>) => {
  const {children, title, defaultOpen = false, ...rest} = props
  const content = <Box>{children}</Box>
  return (
    <CollapsablePanel
      defaultOpen={defaultOpen}
      panelContent={content}
      borderBottom="1px"
      paddingY="16px"
      paddingX="20px"
      borderColor="gray200"
      spacing={20}
      style={{marginBottom: -1}}
      {...rest}
      ref={ref}
    >
      {({onToggle, isOpen}) => {
        return (
          <Flex
            className={pdpAccordionToggle}
            borderRadius="none"
            justifyContent="space-between"
            height="auto"
            width="full"
            alignItems="center"
            onClick={onToggle}
          >
            <Text variant="text6">{title}</Text>
            <ChevronUpIcon
              boxSize="20px"
              style={{
                transform: `rotate(${isOpen ? 0 : 180}deg)`,
                transition: 'transform 200ms ease-out',
              }}
            />
          </Flex>
        )
      }}
    </CollapsablePanel>
  )
})

interface ProductInfoModuleProps {
  productDetail: ReturnType<typeof useProductDetail>
  productInformation: ProductInformation
  pdpAwardSlot: PDPAwardSlot | null
  selectedIndex: number // Add this prop to receive the active tab index
  onTabChange: (index: number) => void // Add this prop to handle tab changes
  reviewsTabRef: React.RefObject<HTMLDivElement> // Add this prop to receive the ref for the reviews tab
}


export const ProductInfoModule = ({
  productDetail,
  productInformation,
  pdpAwardSlot,
  selectedIndex,
  onTabChange,
  reviewsTabRef,
}: ProductInfoModuleProps) => {
  const nutritionModal = useDisclosure()
  const [writeReviewUrl, setWriteReviewUrl] = useState(null)
  const [globalAllergenWords, setGlobalAllergenWords] = useState<string[]>([]);
  const {productId, product} = productDetail
  const {isRegistered} = useCustomerStore()
  const {getCustomSitePreferenceById, customSitePreferences, isCustomSitePreferenceSet} = useGlobalStore()
  const cms = useContentStore()
  const [pdpProductInformationBannerKey, setPdpProductInformationBannerKey] = useState<string | null>(null)

  useEffect(() => {
    const fetchReviewUrl = async () => {
      if (isRegistered && writeReviewUrl === null) {
        const response = await fetch(
          `${getAppOrigin()}/mobify/proxy/ocapi/on/demandware.store/Sites-icelandfoodsuk-Site/default/Product-GetFeefoSmartlinkUrl?productID=${productId}`,
        ).then((response) => response.json())

        setWriteReviewUrl(response.feefoSmartlinkUrl)
      }
    }
    fetchReviewUrl().catch(console.error)
  }, [productId, isRegistered])

  useEffect(() => {
    (async function () {
      if (!isCustomSitePreferenceSet('globalAllergenWords')) {
        await getCustomSitePreferenceById('globalAllergenWords')
      }
      if (Array.isArray(customSitePreferences['globalAllergenWords'])) {
        setGlobalAllergenWords(customSitePreferences['globalAllergenWords'] as Array<string>)
      }
    })()
  })

  const {
    alcoholUnits, 
    alcoholVolume,
    shortDescription,
    dietaryInformation,
    manufacturerAddress,
    manufacturerReturnTo,
    packageType,
    storageInformation,
    cookinginstructions,
    ingredients,
    allergenContains,
    allergenInfo,
    nutritionInformation,
    allergenMayContain,
    pdpProductInformationBanner,
    directionsForUse,
    mandatoryWarning
  } = product

  // Stops PDP from breaking if allergenContains and allergenMayContain are undefined
  const allergens = allergenContains || []
  const allergensProductMayContain = allergenMayContain || []
  const showIngredientsOnMobile = ingredients || allergenContains || allergenMayContain || allergenInfo
  const showNutritionalTable = nutritionInformation && Array.isArray(nutritionInformation?.nutrients) && nutritionInformation.nutrients.length > 0
  const showNutritionInfo = showNutritionalTable || ingredients || allergenInfo || allergenMayContain || allergenContains

  let allergyList
  // If there are allergenContains but no ingredients
  if (!ingredients && allergens.length > 0) {
    const allergies = () => {
      const allergiesArray = []
      for (let i = 0; i < allergenContains?.length; i++) {
        allergiesArray.push(allergenContains[i])
      }
      return allergiesArray.join(', ')
    }
    allergyList = allergies()
  }
  
  let allergensProductMayContainList
  // Format the allergenMayContain list from BM
  if (allergensProductMayContain.length > 0) {
    const possibleAllergens = () => {
      const allergensProductMayContainArray = []
      for (let i = 0; i < allergensProductMayContain?.length; i++) {
        // Capitalize each possible allergen
        const capitalizedAllergen = allergensProductMayContain[i].charAt(0).toUpperCase() + allergensProductMayContain[i].slice(1)
        allergensProductMayContainArray.push(capitalizedAllergen)
      }
      return allergensProductMayContainArray.join(', ')
    }
    allergensProductMayContainList = possibleAllergens()
  }

  // Fetch Individual Product Banner Content from Amplience
  useEffect(() => {
    if (pdpProductInformationBanner) {
      ;(async function () {
        if (!cms.contentById[pdpProductInformationBanner]) {
          await cms.fetchItemByKey(pdpProductInformationBanner)
        }
        setPdpProductInformationBannerKey(pdpProductInformationBanner)
      })()
    }
  }, [])

  return (
    <Flex gap="20px">
      <Stack className={infoLeftColumn} bg="white" flexGrow="1">
        <Tabs
          id="pdpTabs"
          selectedIndex={selectedIndex} // Use the selectedIndex prop to control active tab
          onTabChange={onTabChange} // Handle tab changes
          switcherContent={
            (pdpProductInformationBannerKey || pdpAwardSlot) && (
            <Stack spacing="4px" borderBottom="1px" borderColor="gray200" padding="16px">
              {/* We only want to show one piece of content here: Individual Content > Awards Banners */}
              {pdpProductInformationBannerKey ? <Slot data={cms.contentById[pdpProductInformationBanner]} deliveryKey={pdpProductInformationBanner} /> 
              : pdpAwardSlot ? <Slot data={pdpAwardSlot as unknown as SlotContentItem} /> 
              : null}
            </Stack>
            )
          }
        >
          <TabPanel title="Quick Look" position="relative">
            {showNutritionalTable && (
              <Stack
                display={['flex', 'none']}
                paddingX="20px"
                paddingY="16px"
                borderBottom="1px"
                borderColor="gray200"
                style={{marginBottom: -1}}
              >
                    <Text variant="heading0">Nutrition Info</Text>
                    <Text
                      variant="unstyled"
                      textDecoration="underline"
                      fontSize="sm"
                      cursor="pointer"
                      onClick={() => nutritionModal.onOpen()}
                    >
                      Show full nutritional table
                    </Text>
              </Stack>
            )}
            {cookinginstructions.length > 0 && <CookingInstructions instructions={cookinginstructions} />}
            <Stack spacing="0px">
              {showIngredientsOnMobile && (
                <ProductDetailAccordion
                  display={['block', 'none']}
                  defaultOpen={false}
                  title="Ingredients"
                >
                  <Stack spacing="16px">
                    {ingredients && (
                      <Stack spacing="16px">
                        {ingredients.map((ingredientsText: string, index: number) => (
                          <Text
                            key={index}
                            variant="text3"
                            color="textMuted"
                            lineHeight="short"
                            dangerouslySetInnerHTML={{
                              __html: highlightIngredientsWithAllergens(
                                ingredientsText,
                                [...allergens, ...globalAllergenWords],
                              ),
                            }}
                          />
                        ))}
                      </Stack>
                    )}
                    {!ingredients && allergenContains && (
                      <Stack spacing="16px">
                        <Text variant="unstyled" fontWeight="normal" fontSize="md" lineHeight="base">
                          Allergy Information:
                        </Text>
                        <Text variant="text3" color="textMuted" lineHeight="short">
                          Contains {allergyList}
                        </Text>
                      </Stack>
                    )}
                    {allergenMayContain ? (
                      <Stack spacing="16px">
                        <Text variant="text3" color="textMuted" lineHeight="short">
                          May contain: {allergensProductMayContainList}
                        </Text>
                      </Stack>
                    ): null}
                    {allergenInfo && (
                      <Stack>
                        <Text variant="text3" color="textMuted" lineHeight="short">
                          {allergenInfo}
                        </Text>
                      </Stack>
                    )}
                  </Stack>
                </ProductDetailAccordion>
              )}
              <ProductDetailAccordion
                defaultOpen={false}
                title="Additional Info"
              >
                <Stack spacing="24px">
                  {shortDescription && (
                    <Text variant="text3" lineHeight="shorter">
                      {shortDescription}
                    </Text>
                  )}
                  {dietaryInformation && (
                    <Stack spacing="16px">
                      <Text variant="text3">Dietary Information</Text>
                      <Text variant="text3" color="textMuted" lineHeight="shorter">
                        {dietaryInformation}
                      </Text>
                    </Stack>
                  )}
                  {manufacturerAddress && (
                    <Stack spacing="16px">
                      <Text variant="text3">Manufacturers Address</Text>
                      <Text variant="text3" color="textMuted" lineHeight="shorter">
                        {manufacturerAddress}
                      </Text>
                    </Stack>
                  )}
                  {manufacturerReturnTo && (
                    <Stack spacing="16px">
                      <Text variant="text3">Return To</Text>
                      <Text variant="text3" color="textMuted" lineHeight="shorter">
                        {manufacturerReturnTo}
                      </Text>
                    </Stack>
                  )}
                  {packageType && (
                    <Stack spacing="16px">
                      <Text variant="text3">Package type</Text>
                      <Text variant="text3" color="textMuted" lineHeight="shorter">
                        {packageType}
                      </Text>
                    </Stack>
                  )}
                   {alcoholVolume && (
                    <Stack spacing="16px">
                      <Text variant="text3">Alcohol Volume</Text>
                      <Text variant="text3" color="textMuted" lineHeight="shorter">
                        {alcoholVolume}%
                      </Text>
                    </Stack>
                  )}
                   {alcoholUnits && (
                    <Stack spacing="16px">
                      <Text variant="text3">Alcohol Units</Text>
                      <Text variant="text3" color="textMuted" lineHeight="shorter">
                        {alcoholUnits}
                      </Text>
                    </Stack>
                  )}
                  {storageInformation && (
                    <Stack spacing="16px">
                      <Text variant="text3">Storage information</Text>
                      <Text variant="text3" color="textMuted" lineHeight="shorter">
                        {storageInformation}
                      </Text>
                    </Stack>
                  )}
                  {directionsForUse && (
                    <Stack spacing="16px">
                      <Text variant="text3">Directions for Use</Text>
                      <Text variant="text3" color="textMuted" lineHeight="shorter">
                        {directionsForUse}
                      </Text>
                    </Stack>
                  )}
                  {mandatoryWarning && (
                    <Stack spacing="16px">
                      <Text variant="text3">Warning</Text>
                      <Text variant="text3" color="textMuted" lineHeight="shorter">
                        {mandatoryWarning}
                      </Text>
                    </Stack>
                  )}
                </Stack>
              </ProductDetailAccordion>
            </Stack>
            <Stack padding="20px" spacing="16px">
              <Text variant="text6">{productInformation?.title}</Text>
              <ReactMarkdown
                components={{
                  p: ({...props}) => {
                    return (
                      <Text variant="text1" lineHeight="short">
                        {props?.children}
                      </Text>
                    )
                  },
                }}
              >
                {productInformation?.copy || ''}
              </ReactMarkdown>
            </Stack>
          </TabPanel>
          <TabPanel
            title={`${product.productReviewCount} Reviews`}
            data-test-selector="pdp-reviews"
            id="reviews"
            ref={reviewsTabRef}
          >
            <Box>
              <FeefoReviews
                productId={productId}
                writeReviewUrl={writeReviewUrl}
                hasReviews={!!product.productReviewCount}
              />
            </Box>
          </TabPanel>
        </Tabs>
      </Stack>
      <Stack className={infoRightColumn} bg="white" display={['none', 'flex']} spacing="16px">
        {showNutritionInfo && (
          <>
            <Stack spacing="32px" borderBottom="1px" borderColor="gray200" paddingBottom="24px">
              <Text variant="heading0">Nutrition Info</Text>
                {showNutritionalTable && (
                  <Text
                    variant="unstyled"
                    textDecoration="underline"
                    fontSize="sm"
                    style={{color: '#4a4b4d', cursor: 'pointer'}}
                    onClick={() => nutritionModal.onToggle()}
                  >
                    Show full nutritional table
                  </Text>
                )}
            </Stack>
            {ingredients && (
              <Stack spacing="16px">
                <Text variant="unstyled" fontWeight="normal" fontSize="md" lineHeight="base">
                  Ingredients:
                </Text>
                {ingredients.map((ingredientsText: string, index: number) => (
                  <Text
                    key={index}
                    variant="text3"
                    color="textMuted"
                    lineHeight="short"
                    dangerouslySetInnerHTML={{
                      __html: highlightIngredientsWithAllergens(ingredientsText, [...allergens, ...globalAllergenWords]),
                    }}
                  />
                ))}
              </Stack>
            )}
            {!ingredients && allergenContains && (
              <Stack spacing="16px">
                <Text variant="unstyled" fontWeight="normal" fontSize="md" lineHeight="base">
                  Allergy Information:
                </Text>
                <Text variant="text3" color="textMuted" lineHeight="short">
                  Contains {allergyList}
                </Text>
              </Stack>
            )}
            {allergenMayContain ? (
              <Stack>
                <Text variant="text3" color="textMuted" lineHeight="short">
                May contain: {allergensProductMayContainList}
                </Text>
              </Stack>
            ): null}
            {allergenInfo && (
              <Stack>
                <Text variant="text3" color="textMuted" lineHeight="short">
                  {allergenInfo}
                </Text>
              </Stack>
            )}
            <NutritionalModal nutritionInformation={nutritionInformation} {...nutritionModal} />
          </>
          )}
      </Stack>
    </Flex>
  )
}

const highlightIngredientsWithAllergens = (ingredients: string, allergens?: string[]): string => {
  if (!allergens || !allergens.length) {
    // Adds a line break to separate the block of text
      return ingredients.replace(/May contain/gi, '<br><br>May contain')
  }

  // Splits any string with 'containing' and removes it from the allergen list
  const splitAllergens = allergens.flatMap(allergen => allergen.includes('containing') ? allergen.split(' ').filter(word => word !== 'containing') : [allergen])

  const getTemplate = (allergen: string) =>
    `<b style="text-decoration: underline">${
      allergen.charAt(0).toUpperCase() + allergen.substring(1)
    }</b>`

  for (const allergen of splitAllergens) {
    const regex = new RegExp(`\\b${allergen}\\b`, 'gi')
    ingredients = ingredients.replace(regex, getTemplate(allergen))
  }
  // Adds a line break to separate the block of text
  ingredients = ingredients.replace(/May contain/gi, '<br><br>May contain')
  return ingredients
}